import type { CrmRecord } from './crm'
import { ProspectDuplicateService } from './supabase/services/duplicates.service'

export interface ProspectIdentityValues {
  businessName: string
  phone: string
  whatsapp: string
  email: string
  instagram: string
  facebook: string
  website: string
  googleMaps: string
}

export interface DuplicateCheckResult {
  records: CrmRecord[]
  error: string | null
}

export function normalizeIdentityPhone(input: string) {
  const digits = input.replace(/\D/g, '')
  if (!digits) return ''
  return digits.length === 8 ? `506${digits}` : digits
}

export function normalizeInstagramForCheck(input: string) {
  const trimmed = input.trim()
  if (!trimmed) return ''
  if (trimmed.startsWith('http')) return trimmed.toLowerCase()
  return `https://instagram.com/${trimmed.replace(/^@/, '').replace(/\/+$/, '')}/`.toLowerCase()
}

export function hasMeaningfulIdentity(values: ProspectIdentityValues) {
  return Boolean(
    values.businessName.trim().length >= 2 ||
      normalizeIdentityPhone(values.phone) ||
      normalizeIdentityPhone(values.whatsapp) ||
      values.email.trim() ||
      normalizeInstagramForCheck(values.instagram) ||
      values.facebook.trim() ||
      values.website.trim() ||
      values.googleMaps.trim(),
  )
}

export async function checkProspectDuplicates(
  values: ProspectIdentityValues,
  excludeProspectId: string | null = null,
): Promise<DuplicateCheckResult> {
  try {
    const records = await ProspectDuplicateService.checkExact({
      p_business_name: values.businessName.trim() || null,
      p_phone: normalizeIdentityPhone(values.phone) || null,
      p_whatsapp: normalizeIdentityPhone(values.whatsapp) || null,
      p_email: values.email.trim().toLowerCase() || null,
      p_instagram_url: normalizeInstagramForCheck(values.instagram) || null,
      p_facebook_url: normalizeUrlForCheck(values.facebook) || null,
      p_website_url: normalizeUrlForCheck(values.website) || null,
      p_google_maps_url: normalizeUrlForCheck(values.googleMaps) || null,
      p_exclude_prospect_id: excludeProspectId,
    })

    return { records: records as CrmRecord[], error: null }
  } catch {
    return {
      records: [],
      error:
        'No fue posible verificar duplicados en este momento. Intentá nuevamente antes de guardar.',
    }
  }
}

export async function findSimilarProspectNames(
  businessName: string,
  excludeProspectId: string | null = null,
): Promise<DuplicateCheckResult> {
  if (businessName.trim().length < 3) return { records: [], error: null }

  try {
    const records = await ProspectDuplicateService.checkSimilar({
      p_business_name: businessName.trim(),
      p_exclude_prospect_id: excludeProspectId,
      p_similarity_threshold: 0.45,
    })

    return { records: records as CrmRecord[], error: null }
  } catch {
    return {
      records: [],
      error: 'No fue posible revisar nombres parecidos en este momento.',
    }
  }
}

export function isDuplicateProspectConflict(
  error: { code?: string; message?: string } | null,
) {
  return Boolean(
    error &&
      (error.code === '23505' ||
        error.message?.toUpperCase().includes('DUPLICATE_PROSPECT')),
  )
}

function normalizeUrlForCheck(input: string) {
  const trimmed = input.trim()
  if (!trimmed) return ''
  return trimmed.startsWith('http')
    ? trimmed.toLowerCase()
    : `https://${trimmed}`.toLowerCase()
}
